import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import {
  FLAG_LABELS, flagList, fetchImageQualitySummary, fetchImageQualityRows, requeueImageScore,
  readImageQualitySwitch, writeImageQualitySwitch,
} from '../../lib/imageQuality';

// Main-image quality (catalog ordering step C, owner 2026-10-04).
//
// AI vision scores every catalog product's main image (score-product-images,
// every 2 minutes, 48 at a time). BAD = blurry, a collage of DIFFERENT products,
// a screenshot or a text-heavy catalog page (several views of one product are
// fine), product not clearly visible, under 300 px, or text over the product. This page is
// where the team reads what "bad" caught, asks suppliers for better photos,
// and - once happy - switches the score on. While the switch is off, nothing
// a trader sees changes.
const TABS = ['bad', 'ok', 'good', 'error'];

export default function ConsoleImageQuality({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('bad');
  const [rows, setRows] = useState(null);
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2400); };

  const load = useCallback(async () => {
    try {
      const [s, r, sw] = await Promise.all([fetchImageQualitySummary(), fetchImageQualityRows(tab), readImageQualitySwitch()]);
      setSummary(s); setRows(r); setOn(sw);
    } catch (e) {
      console.error('[ConsoleImageQuality] load failed:', e?.message || e);
      setRows([]);
    }
  }, [tab]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    const { data, error } = await writeImageQualitySwitch(!on, user?.id);
    setBusy(false);
    if (error || !data?.length) { console.error('[ConsoleImageQuality] switch failed:', error?.message); flash(isAr ? 'تعذّر الحفظ.' : 'Could not save.'); return; }
    setOn(!on);
    flash(!on ? (isAr ? 'تقييم الصور مفعّل في ترتيب الكتالوج.' : 'Image quality now drives the catalog order.')
              : (isAr ? 'أوقف تأثير تقييم الصور.' : 'Image quality switched off.'));
  };
  const recheck = async (r) => {
    setBusy(true);
    const { error } = await requeueImageScore([{ source: r.source, product_id: r.product_id }]);
    setBusy(false);
    if (error) { console.error('[ConsoleImageQuality] requeue failed:', error.message); flash(isAr ? 'تعذّر.' : 'Failed.'); return; }
    flash(isAr ? 'سيُعاد الفحص خلال دقيقتين.' : 'Re-check queued (within 2 minutes).');
    load();
  };

  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
  const name = (p) => (isAr ? (p?.name_ar || p?.name_en) : (p?.name_en || p?.name_ar)) || (isAr ? 'لم يعد في الكتالوج' : 'No longer in the catalog');
  const seller = (p) => (p?.seller_name_latin || '').trim() || p?.seller_name || '';
  const link = (r) => (r.source === 'supplier' ? `/products/${r.product_id}` : `/factory/${r.product?.seller_id}/product/${r.product_id}`);
  const tabLabel = { bad: isAr ? 'سيئة' : 'Bad', ok: isAr ? 'مقبولة' : 'OK', good: isAr ? 'جيدة' : 'Good', error: isAr ? 'تعذّر فحصها' : 'Failed' };

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="image-quality">
      <div className="ac-page" style={font}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'جودة صور المنتجات' : 'Product image quality'}</h1>
            <p className="ac-sub">
              {isAr
                ? 'تقييم آلي للصورة الرئيسية لكل منتج. اطلب من المورّدين صوراً أفضل للمنتجات المعلّمة «سيئة».'
                : 'An automatic check of every product\'s main image. Ask suppliers for better photos of the ones marked bad.'}
            </p>
          </div>
        </div>

        <div className="ac-card" style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 260px', fontSize: 13 }}>
            {summary ? (
              <>
                <b>{summary.total - summary.pending - summary.error}</b> / {summary.total} {isAr ? 'تم فحصها' : 'checked'}
                {' · '}{isAr ? 'في الانتظار' : 'pending'} {summary.pending}
                {' · '}<span style={{ color: '#b42318' }}>{isAr ? 'سيئة' : 'bad'} {summary.bad}</span>
                {' · '}{isAr ? 'مقبولة' : 'ok'} {summary.ok}
                {' · '}{isAr ? 'جيدة' : 'good'} {summary.good}
                {summary.error ? <>{' · '}{isAr ? 'تعذّر' : 'failed'} {summary.error}</> : null}
              </>
            ) : '...'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span>
              {isAr ? 'التأثير على ترتيب الكتالوج:' : 'Effect on the catalog order:'}{' '}
              <b style={{ color: on ? '#2D6A4F' : 'var(--ac-muted)' }}>{on == null ? '...' : on ? (isAr ? 'مفعّل' : 'ON') : (isAr ? 'متوقف' : 'OFF')}</b>
            </span>
            <button className={`ac-btn ac-btn-sm${on ? '' : ' ac-btn-primary'}`} disabled={busy || on == null} onClick={toggle}>
              {on ? (isAr ? 'إيقاف' : 'Switch off') : (isAr ? 'تفعيل' : 'Switch on')}
            </button>
          </div>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 12 }}>
          {TABS.map((k) => (
            <button key={k} className={`ac-chip${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>
              {tabLabel[k]}{summary ? ` · ${summary[k] || 0}` : ''}
            </button>
          ))}
        </div>

        {rows == null ? <div className="ac-skel" style={{ height: 90 }} />
          : rows.length === 0 ? <div className="ac-placeholder">{isAr ? 'لا شيء هنا.' : 'Nothing here.'}</div>
          : rows.map((r) => (
            <div key={`${r.source}:${r.product_id}`} className="ac-card" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 12, marginTop: 8, flexWrap: 'wrap' }}>
              <a href={r.image_url} target="_blank" rel="noopener noreferrer">
                <img src={r.image_url} alt="" loading="lazy" style={{ width: 88, height: 88, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--ac-line)' }} />
              </a>
              <div style={{ flex: '1 1 260px', minWidth: 0, fontSize: 13 }}>
                <a href={link(r)} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, color: 'var(--ac-ink)' }}>{name(r.product)}</a>
                <div style={{ color: 'var(--ac-muted)', fontSize: 12, margin: '2px 0 6px' }}>
                  {seller(r.product)} · {r.source === 'supplier' ? (isAr ? 'مورد' : 'supplier') : (isAr ? 'مصنع' : 'factory')}
                  {r.width ? ` · ${r.width}×${r.height}` : ''}{r.score != null ? ` · ${Math.round(r.score * 100)}/100` : ''}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {flagList(r.flags).map((f) => (
                    <span key={f} className="ac-badge">{isAr ? FLAG_LABELS[f].ar : FLAG_LABELS[f].en}</span>
                  ))}
                  {r.last_error ? <span className="ac-badge">{r.last_error}</span> : null}
                </div>
              </div>
              <button className="ac-btn ac-btn-sm" disabled={busy} onClick={() => recheck(r)}>{isAr ? 'إعادة الفحص' : 'Re-check'}</button>
            </div>
          ))}
      </div>
      {toast && <div className="ac-toast" style={font}>{toast}</div>}
    </ConsoleShell>
  );
}
